/**
 * Food you can see. What it is (FoodDef: a shape and colours, worked out from its name, so every menu in the game gets
 * one) is kept apart from this one you bought (FoodInstance: saved with your life) and from how it's put in front of you
 * (the game's side: experience/foodmoment.ts). You pay when you order; hunger comes when you eat. Until you do it's
 * yours, wherever you go: nothing here ever throws food away except eating it.
 */
import { advance, buy, type LifeEvent, type LifeState, type NeedKey, type Purchase } from './life.ts';

export type FoodShape = 'puffs' | 'croissant' | 'bread' | 'pastry' | 'cake' | 'steak' | 'riceplate' | 'swallow' | 'pasta' | 'noodles' | 'soup' | 'burger' | 'pizza' | 'sandwich' | 'wrap' | 'skewer' | 'dumplings' | 'sushi' | 'taco' | 'icecream' | 'hotdish' | 'snack' | 'drink' | 'hotdrink' | 'plate';
export interface FoodDef { shape: FoodShape; colour: number; /** what's beside it or on top */ side: number; /** in the hand when there's no table */ handheld: boolean }

const T = (shape: FoodShape, colour: number, side: number, handheld = false): FoodDef => ({ shape, colour, side, handheld });
// (first match wins: the specific before the general, and a meal by its main thing, not its drink)
const TABLE: [RegExp, FoodDef][] = [
  [/puff-?puff|akara|beignet|takoyaki|doughnut|donut/i, T('puffs', 0xb9722a, 0x8a5a2a, true)],
  [/croissant|pain au chocolat/i, T('croissant', 0xcf8f3e, 0xe8c07a, true)],
  [/ice cream|gelato/i, T('icecream', 0xf2dcc8, 0xc98a4a, true)],
  [/steak|short rib|duck|roast/i, T('steak', 0x5a2c1a, 0xe2b64a)],
  [/jollof|fried rice|biryani/i, T('riceplate', 0xc4421f, 0xd9a052)],
  [/amala|eba|egusi|fufu|pounded yam|semo|ewedu/i, T('swallow', 0xb79a5e, 0x6a7a2a)],
  [/rice|curry|bento|plat du jour/i, T('riceplate', 0xf0e8d4, 0x8a3a1e)],
  [/ramen|noodle|udon|soba|pho|lo mein/i, T('noodles', 0xe8c878, 0xc89a54)],
  [/pasta|spaghetti|carbonara|lasagn|tagliat/i, T('pasta', 0xe8c878, 0xb8381e)],
  [/soup|stew|chowder|broth/i, T('soup', 0xb5763a, 0xe8d6b0)],
  [/burger/i, T('burger', 0x5a2c1a, 0xd9a35a, true)],
  [/pizza|slice/i, T('pizza', 0xe2b64a, 0xb8381e, true)],
  [/shawarma|burrito|wrap|galette|crêpe|crepe|duck rolls/i, T('wrap', 0xe2c08a, 0x7a4a22, true)],
  [/taco/i, T('taco', 0xe2b64a, 0x7a4a22, true)],
  [/suya|skewer|kebab|yakitori|hawthorn|brochette/i, T('skewer', 0x6a2f18, 0xd8382a, true)],
  [/dumpling|xiaolongbao|shengjian|buns|bao|gyoza|moi moi|samosa|spring roll/i, T('dumplings', 0xf0e6d0, 0xc89a54, true)],
  [/sushi|nigiri|onigiri|maki/i, T('sushi', 0xf4f0e6, 0xd9603a, true)],
  [/sandwich|croque|jambon|bagel|toast|chopped cheese|lox|hot dog|sausage|bacon/i, T('sandwich', 0xd9a35a, 0xc0392b, true)],
  [/meat pie|pie|quiche|tart|gala/i, T('pastry', 0xcf9a4e, 0x8a5a2a, true)],
  [/cake|gâteau|dessert|cookie|biscuit|cantucci/i, T('cake', 0xf0dcc8, 0x6a3a22, true)],
  [/baguette|bread|sourdough|focaccia|fougasse|campagne|khubz|loaf/i, T('bread', 0xc98f45, 0xe8c07a, true)],
  [/pancake|waffle/i, T('cake', 0xdca45a, 0x8a4a1a)],
  [/fish|prawn|shrimp|chicken|wings|beef|meat|asun|grill/i, T('hotdish', 0x9a5a2a, 0x3f7a35)],
  [/chips|crisps|plantain|snack|olives|nuts|dates|cheese|comté|sweets|chocolate/i, T('snack', 0xe2b64a, 0xc98a3a, true)],
  [/coffee|café|latte|espresso|tea|matcha|pour-over|cold brew/i, T('hotdrink', 0x5a3418, 0xf4efe0, true)],
  [/beer|lager|stout|wine|bordeaux|cider|pastis|cocktail|chapman|soda|lacasera|malt|zobo|juice|water|coconut|horchata|sake|drink/i, T('drink', 0xc9782a, 0xf4f0e6, true)],
];
const ANY = T('plate', 0xc89a54, 0x8a5a2a);
export function foodDef(label: string): FoodDef { for (const [re, d] of TABLE) if (re.test(label)) return d; return ANY; }

export interface FoodInstance {
  id: string;
  label: string;
  needs: Partial<Record<NeedKey, number>>;
  /** how long it takes to eat */
  minutes: number;
  /** where it came from */
  from: string;
  at: number;
}

export const mealsOf = (s: LifeState): FoodInstance[] => (s.meals ??= []);

/** Order it: paid for, and now it's yours. Hunger comes when you eat it. null: you can't afford it. */
export function orderFood(s: LifeState, p: Purchase, from: string): { food: FoodInstance; events: LifeEvent[] } | null {
  const r = buy(s, { ...p, needs: undefined, health: undefined, minutes: 2 });
  if (!r) return null;
  const food: FoodInstance = { id: `f${s.minutes}-${mealsOf(s).length}-${Math.floor(Math.random() * 1e6).toString(36)}`, label: p.label, needs: p.needs ?? {}, minutes: p.minutes ?? 10, from, at: s.minutes };
  mealsOf(s).push(food);
  return { food, events: r.events };
}

/** Eat it: the hunger, the time it takes, and it's gone. Anything already eaten (a second tap) does nothing. */
export function eatFood(s: LifeState, id: string): LifeEvent[] | null {
  const list = mealsOf(s), i = list.findIndex((f) => f.id === id);
  if (i < 0) return null;
  const [f] = list.splice(i, 1);
  for (const [k, v] of Object.entries(f.needs)) s.needs[k as NeedKey] = Math.max(0, Math.min(100, s.needs[k as NeedKey] + (v as number)));
  return advance(s, f.minutes);
}
